// crossFieldRules — validation rules that compare a field against ANOTHER field,
// shared by AddFormV1 and EditFormV1 so both forms enforce them identically.
//
// The historical cross-field rules (greaterThanField, dateAfterField, …) live
// inline in each form and resolve the referenced key as a SIBLING: the same
// addRow row when the rule sits inside one, the top level otherwise. That is
// wrong for a rule that must reach OUT of its row — e.g. "the experience typed
// on a relevant-skill row may not exceed the candidate's overall experience",
// where the row field points at a top-level field.
//
// resolveScopedFieldPath fixes that by using the SAME scoping rule the render
// side already applies to `showIf`: renderField builds a `prefixFor(key)`
// closure that returns the row prefix ([listName, rowIndex]) only when the
// referenced key is one of the row's own fields (opts.rowFieldKeys), and null
// for anything else — which resolves to the top-level path. Passing that same
// closure in here means a rule and a Show Condition can never disagree about
// which field a key refers to.

// Local emptiness check — mirrors the forms' own `empty` helper. Kept here so
// this module has no import back into either form (they both import from it).
const isBlank = (v) => v === undefined || v === null || v === ''
  || (Array.isArray(v) && v.length === 0);

/**
 * resolveScopedFieldPath — the antd name path a rule's referenced field key
 * resolves to.
 *
 * @param {Array|string} name      the CURRENT field's absolute name path
 * @param {string} key             the referenced field key (dot notation allowed)
 * @param {Function} [prefixFor]   renderField's showIf prefix resolver
 * @returns {Array} absolute name path
 */
export function resolveScopedFieldPath(name, key, prefixFor) {
  const parts = String(key ?? '').includes('.') ? String(key).split('.') : [key];
  if (typeof prefixFor === 'function') {
    const prefix = prefixFor(key);
    return Array.isArray(prefix) && prefix.length ? [...prefix, ...parts] : parts;
  }
  // No prefix resolver (e.g. a repeat-scalar item): keep the historical
  // sibling-first behaviour so nothing that worked before changes.
  return Array.isArray(name) && name.length > 1 ? [...name.slice(0, -1), ...parts] : parts;
}

/**
 * maxFromFieldRule — `{ type: 'maxFromField', field: '<otherFieldKey>', message }`
 *
 * The value may not exceed the CURRENT value of another field. Generic: which
 * field caps which is admin config (Form Groups → field → validations), never
 * code. Fails OPEN whenever either side is blank or non-numeric — a validation
 * rule must never block a save it cannot actually evaluate.
 */
export function maxFromFieldRule({ label, message, rule = {}, value, form, name, prefixFor }) {
  const referencedKey = rule.field ?? rule.compareField ?? value;
  return {
    validator: async (_, input) => {
      if (!referencedKey || isBlank(input)) return Promise.resolve();
      const other = form?.getFieldValue?.(resolveScopedFieldPath(name, referencedKey, prefixFor));
      if (isBlank(other)) return Promise.resolve();
      const maximum = Number(other);
      const current = Number(input);
      if (Number.isNaN(maximum) || Number.isNaN(current)) return Promise.resolve();
      if (current <= maximum) return Promise.resolve();
      return Promise.reject(new Error(message ?? `${label} must not exceed ${referencedKey}`));
    },
  };
}

/**
 * maxFromFieldDependency — the absolute name path a maxFromField rule depends
 * on, so antd re-runs the rule when the referenced field changes.
 */
export function maxFromFieldDependency(rule = {}, name, prefixFor) {
  const referencedKey = rule.field ?? rule.compareField ?? rule.value;
  if (!referencedKey) return null;
  return resolveScopedFieldPath(name, referencedKey, prefixFor);
}

export default { resolveScopedFieldPath, maxFromFieldRule, maxFromFieldDependency };
